<template>
	<div class="mt-2 amelipro-postal-address__city-row">
		<div class="d-flex flex-column flex-md-row align-md-center">
			<div
				v-if="autoCompleteList.length > 0"
				class="mr-sm-3 postal-code-field"
			>
				<AmeliproAutoCompleteField
					:id="`${groupId}-postal-code`"
					ref="inputPostalCodeField"
					v-model="internalValue.postalCode"
					:aria-describedby="postalCodeErrorMessage !== '' ? `${groupId}-postal-code-error` : undefined"
					:aria-invalid="postalCodeErrorMessage !== '' ? true : false"
					:aria-required="ariaRequired"
					autocomplete="address-level2"
					:disabled="disabled"
					hide-error-message
					:items="postalCodeAutoCompleteList(autoCompleteList)"
					label="Code Postal"
					:readonly="readonly"
					:rules="inputRules('postalCodeRules')"
					@blur="emitEvent('blur', 'city')"
					@change="emitEvent('change', 'city')"
					@focus="postalCodeFocused = true"
					@update:error="emitEvent('update:error', 'city')"
				/>
			</div>
			<div
				v-else
				class="mr-sm-3 postal-code-field"
			>
				<label
					class="text-body-1"
					:for="`${groupId}-postal-code`"
				>
					Code postal&nbsp;:&nbsp;
					<span v-if="ariaRequired">
						<span aria-hidden="true">
							*
						</span>
						<span class="d-sr-only">
							&nbsp;Champ obligatoire
						</span>
					</span>
				</label>

				<VTextField
					:id="`${groupId}-postal-code`"
					ref="inputPostalCodeField"
					v-model="internalValue.postalCode"
					:aria-describedby="postalCodeErrorMessage !== '' ? `${groupId}-postal-code-error` : undefined"
					:aria-invalid="postalCodeErrorMessage !== '' ? true : false"
					:aria-required="ariaRequired"
					autocomplete="postal-code"
					:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
					class="pt-0"
					color="ap-blue darken-1"
					dense
					:disabled="disabled"
					hide-details
					outlined
					:readonly="readonly"
					:rules="inputRules('postalCodeRules')"
					@blur="emitEvent('blur')"
					@change="emitEvent('change')"
					@focus="postalCodeFocused = true"
					@update:error="emitEvent('update:error')"
				/>
			</div>
			<AmeliproMessage
				v-if="postalCodeErrorMessage !== '' && !breakpointHelper.isDesktop()"
				:id="`${groupId}-postal-code-error`"
				class="mb-0"
				no-icon
				text
				type="error"
			>
				<p class="mb-0">
					{{ postalCodeErrorMessage }}
				</p>
			</AmeliproMessage>
			<div
				v-if="autoCompleteList.length > 0"
				class="city-field"
			>
				<AmeliproAutoCompleteField
					:id="`${groupId}-city`"
					ref="inputCityField"
					v-model="internalValue.city"
					:aria-describedby="cityErrorMessage !== '' ? `${groupId}-city-error` : undefined"
					:aria-invalid="cityErrorMessage !== '' ? true : false"
					:aria-required="ariaRequired"
					autocomplete="address-level2"
					:disabled="disabled"
					hide-error-message
					:items="cityAutoCompleteList(autoCompleteList)"
					label="Commune"
					:readonly="readonly"
					:rules="inputRules('cityRules')"
					@blur="emitEvent('blur', 'postalCode')"
					@change="emitEvent('change', 'postalCode')"
					@focus="cityFocused = true"
					@update:error="emitEvent('update:error', 'postalCode')"
				/>
			</div>

			<div
				v-else
				class="city-field"
			>
				<label
					class="text-body-1"
					:for="`${groupId}-city`"
				>
					Commune&nbsp;:&nbsp;
					<span v-if="ariaRequired">
						<span aria-hidden="true">
							*
						</span>
						<span class="d-sr-only">
							&nbsp;Champ obligatoire
						</span>
					</span>
				</label>
				<VTextField
					:id="`${groupId}-city`"
					ref="inputCityField"
					v-model="internalValue.city"
					:aria-describedby="cityErrorMessage !== '' ? `${groupId}-city-error` : undefined"
					:aria-invalid="cityErrorMessage !== '' ? true : false"
					:aria-required="ariaRequired"
					autocomplete="address-level2"
					:background-color="disabled ? 'ap-grey lighten-2' : 'ap-white'"
					class="pt-0"
					color="ap-blue darken-1"
					dense
					:disabled="disabled"
					hide-details
					outlined
					:readonly="readonly"
					:rules="inputRules('cityRules')"
					@blur="emitEvent('blur')"
					@change="emitEvent('change')"
					@focus="cityFocused = true"
					@update:error="emitEvent('update:error')"
				/>
			</div>
		</div>
		<AmeliproMessage
			v-if="postalCodeErrorMessage !== '' && breakpointHelper.isDesktop()"
			:id="`${groupId}-postal-code-error`"
			class="mb-0"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ postalCodeErrorMessage }}
			</p>
		</AmeliproMessage>
		<AmeliproMessage
			v-if="cityErrorMessage !== ''"
			:id="`${groupId}-city-error`"
			class="mb-0"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ cityErrorMessage }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
/*eslint-disable max-lines*/
	import { InputPostalAddressAutoCompleteItem, InputPostalAddressCityRowGroup } from './types';
	import { PropType, computed, ref, watch } from 'vue';
	import AmeliproAutoCompleteField from '../AmeliproAutoCompleteField';
	import AmeliproMessage from '../AmeliproMessage';
	import { AutoCompleteItem } from '../AmeliproAutoCompleteField/types';
	import BreakpointHelper from '../../helpers/BreakpointHelper';
	import { InputPostalAddressField } from '../AmeliproPostalAddressField/types';
	import { VTextField } from 'vuetify/lib';
	import { ValidationRule } from '../../rules/types';
	import { convertToHex } from '../../functions/convertToHex';
	import { required } from '../../rules/required';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		autoCompleteList: {
			type: Array as PropType<InputPostalAddressAutoCompleteItem[]>,
			default: () => [],
		},
		cityRules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		postalCodeRules: {
			type: Array as PropType<ValidationRule[]>,
			default: () => [],
		},
		readonly: {
			type: Boolean,
			default: false,
		},
		value: {
			type: Object as PropType<InputPostalAddressCityRowGroup>,
			default: () => ({
				postalCode: undefined,
				city: undefined,
			}),
		},
	});
	const breakpointHelper = new BreakpointHelper();

	// v-model
	const internalValue = computed({
		get: (): InputPostalAddressCityRowGroup => props.value,
		set: (newValue: InputPostalAddressCityRowGroup): void => {
			emit('input', newValue);
		},
	});
	const setInternalValue = (autocomplete?: string): void => {
		if (autocomplete === 'city') {
			internalValue.value.city = String(internalValue.value.postalCode);
		}

		if (autocomplete === 'postalCode') {
			internalValue.value.postalCode = internalValue.value.city;
		}

		if (postalCodeFocused.value) {
			postalCodeFocused.value = false;
		}

		if (cityFocused.value) {
			cityFocused.value = false;
		}
	};

	type eventType = 'blur' | 'change' | 'update:error';
	const emit = defineEmits(['blur', 'change', 'input', 'update:error']);
	const emitEvent = (eventName: eventType, autocomplete?: string): void => {
		setInternalValue(autocomplete);
		emit(eventName, internalValue.value);
		updateErrorMessages();
	};
	const updateErrorMessages = (): void => {
		postalCodeErrorMessages.value = inputPostalCodeField.value.messagesToDisplay as string[];
		cityErrorMessages.value = inputCityField.value.messagesToDisplay as string[];
	};

	const inputRules = (ruleKey: 'cityRules' | 'postalCodeRules'): ValidationRule[] => {
		const rules = [
			...props[ruleKey],
		];

		if (props.ariaRequired) {
			rules.push(required);
		}

		return rules;
	};

	/**
	 * <ref>inputPostalCodeField est tantôt :
	 * - un VTextField (si autoCompleteList est vide)
	 * - un AmeliproAutoCompleteField (si autoCompleteList n'est pas vide)
	 *
	 */
	const inputPostalCodeField = ref<InputPostalAddressField>({
		hasMessages: false,
		messagesToDisplay: [],
		valid: false,
	});
	watch(() => inputPostalCodeField.value.messagesToDisplay, () => {
		postalCodeErrorMessages.value = inputPostalCodeField.value.messagesToDisplay as string[] ?? [];
	});
	const postalCodeAutoCompleteList = (list: InputPostalAddressAutoCompleteItem[]): AutoCompleteItem[] => {
		const autoCompleteItemList: AutoCompleteItem[] = [];
		list.forEach((element) => {
			autoCompleteItemList.push({
				disabled: element.disabled,
				text: `${element.postalCode}`,
				value: element,
			});
		});
		return autoCompleteItemList;
	};
	const postalCodeErrorMessages = ref<string[]>([]);
	const postalCodeErrorMessage = computed<string>(() => postalCodeErrorMessages.value[0] ?? '');
	const postalCodeFocused = ref(false);
	const displayCityError = computed(() => !inputCityField.value?.valid && inputCityField.value?.hasMessages);
	const inputCityBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (cityFocused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayCityError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});

	/**
	 * <ref>inputCityField est tantôt :
	 * - un VTextField (si autoCompleteList est vide)
	 * - un AmeliproAutoCompleteField (si autoCompleteList n'est pas vide)
	 *
	 */
	const inputCityField = ref<InputPostalAddressField>({
		hasMessages: false,
		messagesToDisplay: [],
		valid: false,
	});
	watch(() => inputCityField.value.messagesToDisplay, () => {
		cityErrorMessages.value = inputCityField.value.messagesToDisplay as string[] ?? [];
	});
	const cityAutoCompleteList = ((list: InputPostalAddressAutoCompleteItem[]): AutoCompleteItem[] => {
		const autoCompleteItemList: AutoCompleteItem[] = [];
		list.forEach((element) => {
			autoCompleteItemList.push({
				disabled: element.disabled,
				text: `${element.city} (${element.postalCode})`,
				value: element,
			});
		});
		return autoCompleteItemList;
	});
	const cityErrorMessages = ref<string[]>([]);
	const cityErrorMessage = computed<string>(() => cityErrorMessages.value[0] ?? '');
	const cityFocused = ref(false);
	const displayPostalCodeError = computed(() => !inputPostalCodeField.value?.valid && inputPostalCodeField.value?.hasMessages);
	const inputPostalCodeBorderStyle = computed<string>(() => {
		let borderColor = convertToHex('ap-grey darken-1');

		if (postalCodeFocused.value) {
			borderColor = convertToHex('ap-blue darken-1');
		}

		if (displayPostalCodeError.value) {
			borderColor = convertToHex('ap-red');
		}

		if (props.disabled) {
			borderColor = convertToHex('ap-grey lighten-2');
		}

		return `1px solid ${borderColor}`;
	});

</script>

<style lang="scss" scoped>
.postal-code-field {
	min-width: 120px;
	width: 120px;

	:deep(.v-input__slot) {
		min-height: $input-min-height !important;
		border: v-bind(inputPostalCodeBorderStyle);
		border-radius: $input-radius;

		fieldset {
			border: 0;
		}
	}
}

.city-field {
	min-width: 200px;
	width: 100%;

	:deep(.v-input__slot) {
		border: v-bind(inputCityBorderStyle);
	}
}

.city-field,
.postal-code-field {
	:deep(.v-input__slot) {
		min-height: $input-min-height !important;
		border-radius: $input-radius;

		fieldset {
			border: 0;
		}
	}

	label {
		margin-bottom: 4px;
		font-weight: $label-font-weight;
	}
}
</style>
